import type { TutorSource } from './types';

/**
 * Internal citation tags the model may leave in prose.
 *
 * - TIMED tags `[lecture 01:07]` / `[lecture 01:07-01:29]` carry real seconds and are KEPT
 *   verbatim (never invented, never rewritten here).
 * - UNTIMED tags `[transcript:<slug>/<id>]`, `[coursebook:ch3.2]`, `[reading:...]`, ... are
 *   internal refs. They must never reach the learner as raw ids: when a matching entry exists in
 *   the message's SOURCES list they become a numbered superscript (the same number the source
 *   chip carries), otherwise they are dropped.
 *
 * Fenced/inline code is left untouched, as are markdown links `[x](url)` and plain brackets
 * such as `[1]`.
 */
const KINDS = [
  'transcript', 'reading', 'coursebook', 'lecture', 'lab', 'test', 'quiz', 'source', 'doc', 'video', 'ref',
];
const KIND_ALT = KINDS.join('|');
// `lecture 01:07` / `lecture 01:07-01:29` (timed) is NOT matched: untimed needs `kind:` directly.
const UNTIMED = new RegExp(`([ \\t]*)\\[\\s*(${KIND_ALT})\\s*:\\s*([^\\]\\n]{1,200}?)\\s*\\](?!\\()`, 'gi');
const SUPERSCRIPT = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'];

const toSuperscript = (n: number): string =>
  String(n).split('').map((d) => SUPERSCRIPT[Number(d)]).join('');

const norm = (s: string): string => s.trim().toLowerCase();

/** 1-based index of the source whose id the tag's ref points at, or 0. */
function sourceIndex(kind: string, ref: string, sources?: TutorSource[]): number {
  if (!sources?.length) return 0;
  const full = norm(`${kind}:${ref}`);
  const r = norm(ref);
  const tail = r.includes('/') ? r.slice(r.lastIndexOf('/') + 1) : r;
  const i = sources.findIndex((s) => {
    const id = s.id ? norm(s.id) : '';
    return !!id && (id === full || id === r || id === tail || id.endsWith(`/${tail}`) || id.endsWith(`:${tail}`));
  });
  return i + 1;
}

/** Drop an unfinished trailing `[...` that could become a citation tag, so it never flashes. */
function hideIncompleteTail(text: string): string {
  const open = text.lastIndexOf('[');
  if (open < 0) return text;
  const rest = text.slice(open + 1);
  if (rest.includes(']') || rest.includes('\n') || rest.length > 220) return text;
  const head = (/^[a-z]*/i.exec(rest)?.[0] ?? '').toLowerCase();
  const couldBeTag = KINDS.some((k) => k.startsWith(head) && (head.length > 0 || rest === ''))
    || KINDS.some((k) => head === k);
  if (!couldBeTag) return text;
  return text.slice(0, open).replace(/[ \t]+$/, '');
}

function convert(segment: string, sources: TutorSource[] | undefined, markers: boolean): string {
  return segment.replace(UNTIMED, (match, lead: string, kind: string, ref: string, offset: number, whole: string) => {
    if (/^lecture$/i.test(kind) && /^\d{1,3}:\d\d/.test(ref)) return match; // defensive: timed
    const idx = markers ? sourceIndex(kind, ref, sources) : 0;
    if (idx) return toSuperscript(idx);
    const next = whole[offset + match.length];
    if (next === undefined || /[\s.,;:!?)\]]/.test(next)) return '';
    return lead;
  });
}

export interface CleanOptions {
  sources?: TutorSource[];
  /** Mid-stream: also hide an incomplete trailing tag. */
  streaming?: boolean;
  /** Render numbered superscripts for matched sources (false for clipboard text). */
  markers?: boolean;
}

export function cleanCitationTags(content: string, opts: CleanOptions = {}): string {
  if (!content) return content;
  const { sources, streaming = false, markers = true } = opts;
  // Split on fenced + inline code so code samples are never rewritten.
  const parts = content.split(/(```[\s\S]*?(?:```|$)|~~~[\s\S]*?(?:~~~|$)|`[^`\n]*`)/g);
  let out = parts.map((p, i) => (i % 2 === 1 ? p : convert(p, sources, markers))).join('');
  if (streaming) {
    // Only act when the unfinished tail is outside an open code fence.
    const fences = (out.match(/^ {0,3}(```|~~~)/gm) ?? []).length;
    if (fences % 2 === 0 && (out.match(/`/g) ?? []).length % 2 === 0) out = hideIncompleteTail(out);
  }
  return out;
}
